import { Box, Menu } from '@mantine/core';
import { useApiDeleteApiKey } from '@repo/api-codegen';
import type { ApiKeyOutV2 } from '@repo/api-codegen/v2';
import {
	apiListApiKeysV2QueryKey,
	useApiListApiKeysV2,
} from '@repo/api-codegen/v2';
import { SettingsBlock, SettingsButton } from '@repo/common/components';
import { UserRole } from '@repo/common/enums/UserRole';
import { Icon, IconButton, ResourceIcon, Text } from '@repo/foundations';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { queryClient } from '../../../api';
import { useSuspenseFeatureAccess } from '../../../api/hooks/workspace/useSuspenseFeatureAccess';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { EmptyState } from '../../EmptyState';
import { LoadingSpinner } from '../../LoadingSpinner';
import { openConfirmModal, openModal } from '../../ModalManager';
import { UpgradeButton } from '../UpgradeButton';
import { ApiFiltersGenerator } from './ApiFiltersGenerator/ApiFiltersGenerator';
import { ApiKeyModal } from './ApiKeyModal';

export function ApiSettings() {
	const { hasPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'APIKeys.Read',
	});
	const { apiAccess } = useSuspenseFeatureAccess();
	const { data: keys, isLoading } = useApiListApiKeysV2(
		{},
		{
			select: (data) => data.results,
		}
	);

	const openCreateKeyModal = useCallback(() => {
		openModal({
			title: 'Create API key',
			children: <ApiKeyModal />,
		});
	}, []);

	const openEditKeyModal = useCallback((key: ApiKeyOutV2) => {
		openModal({
			title: 'Rename API key',
			children: <ApiKeyModal apiKey={key} />,
		});
	}, []);

	const { mutate: deleteKey } = useApiDeleteApiKey({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiListApiKeysV2QueryKey({}),
			});
		},
	});

	const handleDeleteKey = useCallback(
		(apiKey: ApiKeyOutV2) => {
			openConfirmModal({
				title: 'Delete API key?',
				children: <Text>You can't undo this action.</Text>,
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				labels: { confirm: 'Delete API key', cancel: 'Cancel' },
				onConfirm: () =>
					deleteKey({
						pathParams: { apiKeyId: apiKey.id },
					}),
			});
		},
		[deleteKey]
	);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (!apiAccess) {
		return (
			<EmptyState
				title="Upgrade to access the API"
				description="You can use an API to programatically access information in Secoda."
				illustrationName="upgrade"
				includeGoBack={false}
				stateHeight="50vh"
				size="lg"
				withActions={<UpgradeButton feature="API" size="md" />}
			/>
		);
	}

	return (
		<Box>
			<Box>
				<SettingsButton
					title={
						keys?.length === 0
							? 'No API keys'
							: `${keys?.length} API key${keys?.length === 1 ? '' : 's'}`
					}
				>
					<Box style={{ position: 'relative' }}>
						{apiAccess ? (
							<IconButton
								iconName="plus"
								variant="tertiary"
								size="md"
								onClick={openCreateKeyModal}
								disabled={!hasPermission}
							/>
						) : (
							<UpgradeButton feature="API" />
						)}
					</Box>
				</SettingsButton>
				{keys?.map((key) => (
					<SettingsButton
						leftComponent={<ResourceIcon iconName="key" />}
						key={key.id}
						title={key.description}
						description={
							<>
								{key.expires_at && (
									<Text inherit>
										{`Expiry date `}
										<Text
											span
											inherit
											color={
												dayjs(key.expires_at).isBefore(dayjs())
													? 'text/critical/default'
													: undefined
											}
										>
											{dayjs(key.expires_at).format('MMM D, YYYY')}
										</Text>
									</Text>
								)}
								<Text inherit>
									{key.times_used > 0 &&
										`Used ${key.times_used} ${key.times_used === 1 ? 'time' : 'times'}`}
									{key.last_used &&
										` · Last used ${dayjs(key.last_used).format('MMM D, YYYY HH:mm:ss')} by ${key.last_used_ip}`}
								</Text>
								{key.user_email && <Text inherit>{key.user_email}</Text>}
							</>
						}
					>
						<Menu position="bottom-end">
							<Menu.Target>
								<IconButton
									iconName="dots"
									variant="tertiary"
									disabled={!hasPermission}
								/>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item
									icon={<Icon name="pencil" />}
									onClick={() => openEditKeyModal(key)}
									disabled={!hasPermission}
								>
									Edit key
								</Menu.Item>
								<Menu.Item
									icon={<Icon name="trash" />}
									onClick={() => handleDeleteKey(key)}
									disabled={!hasPermission}
									color="red"
								>
									Delete key
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					</SettingsButton>
				))}
			</Box>

			<Box mt="xl">
				<SettingsButton
					title="API Specification"
					description="View the complete OpenAPI specification"
					buttonText="View schema"
					onClick={() => {
						window.open(
							'https://api.secoda.co/api/schema/redoc/#tag/Resource/paths/~1resource~1catalog/get',
							'_blank'
						);
					}}
				/>
				<SettingsBlock
					title="API Tool"
					description="Generate filters for the Resource API endpoint"
				>
					<ApiFiltersGenerator />
				</SettingsBlock>
			</Box>
		</Box>
	);
}
